import { Statuses } from '@geee-be/core';
import type { Person } from '@local/common-lib';
import type { FC } from 'react';
import { useParams } from 'react-router-dom';
import { MiddleSpinner } from '../../components/middle-spinner/index.js';
import { useAxios } from '../../hooks/use-axios.js';
import { useSetTitle } from '../../hooks/use-title.js';
import type { ExistingEntityViewProps } from '../../utils/types.js';
import { ErrorPage } from '../error/index.js';
import { NotFoundPage } from '../not-found/index.js';

const ExistingPersonPageInner: FC<{
  id: string;
  view: FC<ExistingEntityViewProps<Person.View>>;
}> = ({ id, view: View }) => {
  const [response, refresh, fetchNow] = useAxios<Person.View>(
    `/api/v1/person/${encodeURIComponent(id)}`,
  );
  useSetTitle(
    response.loading
      ? 'Loading...'
      : response.error
        ? 'Error'
        : `${response.data.firstName} ${response.data.lastName.toUpperCase()}`,
  );

  if (response.loading) return <MiddleSpinner />;
  if (response.error?.response?.status === Statuses.NOT_FOUND) {
    return <NotFoundPage />;
  }
  if (response.error) return <ErrorPage error={response.error} />;

  return (
    <View data={response.data} fetchNow={fetchNow} id={id} refresh={refresh} />
  );
};

export const ExistingPersonPage: FC<{
  view: FC<ExistingEntityViewProps<Person.View>>;
}> = ({ view }) => {
  const { id } = useParams<{ id: string }>();
  if (!id) return <ErrorPage error={new Error('Invalid URL')} />;

  return <ExistingPersonPageInner id={id} view={view} />;
};
